import React from 'react';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Popover, PopoverButton, PopoverPanel, Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { FiCheckCircle, FiAlertCircle, FiUser, FiMapPin, FiClock, FiBriefcase, FiGlobe, FiRefreshCw, FiSave, FiCalendar, FiChevronLeft, FiChevronRight, FiChevronDown, FiPlus, FiMinus, FiArrowRight } from 'react-icons/fi';
import { Truck, Calendar as LucideCalendar } from 'lucide-react';
import { DriverSelectionPanel } from '../../components/DriverSelectionModal';
import TripsSelectionTab from '../../components/TripsSelectionTab';
import CapPlanPanel from '../capPlan/CapPlanPanel';
import FleetMappingPanel from '../fleetMapping/FleetMappingPanel';
import { usePublishAccess } from '../../contexts/PublishAccessContext';
import { displayedServerEnvironment } from '../../utils/nemtEnvironment';
import { schedulerApiFetch } from '../../api/schedulerClient';

dayjs.extend(utc);
dayjs.extend(timezone);




export default function UnifiedSchedulerDrawer({
  isOpen,
  onClose,
  onStart, // (selectedDriverIds, frozenDriverIds, freezePreAssignDriverIds, selectedTripIds, perDriverMaxLoad, preservePortalBuckets, useCapPlan, useFleetMapping) => void
  
  // From StartPlanningSelectionModal
  schedulerApiUrl,
  getAuthToken,

  // All Config Props
  activeConfigTab,
  setActiveConfigTab,
  currentConfig,
  configFormData,
  handleConfigChange,
  leglockNotification,
  setLeglockNotification,
  fetchCompanyDetails,
  isSyncing,
  isSavingApiSettings,
  saveApiSettings,
  configDirtyFields,
  getConfigFieldDisplay,
  syncMessage,
  syncTrigger,
  companyDetails,
  customPlanningDate,
  setCustomPlanningDate,
  customPlanningEndDate,
  setCustomPlanningEndDate,
  useCustomPlanningTimes,
  setUseCustomPlanningTimes,
  use2HourWindow,
  setUse2HourWindow,
  dateValidationError,
  companyTimezone,
  companyCurrentTime,
  formatCompanyTime,
  get2HourWindowEndTime,
  isStarting,
}) {
  const { adminModeUnlocked } = usePublishAccess();
  // Navigation Tabs for the Right Pane
  // 'drivers' | 'trips'
  const [activeRightTab, setActiveRightTab] = React.useState('drivers');

  // Reset internal tab logic when opened
  const [driverSelection, setDriverSelection] = React.useState(null);
  const [liveDriverSelection, setLiveDriverSelection] = React.useState(null);
  const [selectedTripIds, setSelectedTripIds] = React.useState([]);
  const [useTripSelection, setUseTripSelection] = React.useState(false);
  const [useCapPlan, setUseCapPlan] = React.useState(false);
  const capPlanFlushRef = React.useRef(null);
  const [useFleetMapping, setUseFleetMapping] = React.useState(false);
  const [fleetMappingAvailable, setFleetMappingAvailable] = React.useState(false);
  const fleetMappingFlushRef = React.useRef(null);
  const [fleetMappingStartError, setFleetMappingStartError] = React.useState('');
  const handleFleetMappingFlushReady = React.useCallback((fn) => { fleetMappingFlushRef.current = fn; }, []);
  // Driver rows (and so the selection) depend on the mapping; force a reload from the Drivers tab.
  const resetDriverSelection = React.useCallback(() => {
    setLiveDriverSelection(null);
    setDriverSelection(null);
  }, []);
  const fleetMappingChangedRef = React.useRef(false);
  const [fleetMapperProfiles, setFleetMapperProfiles] = React.useState([]);
  const [fleetMapperProfilesLoading, setFleetMapperProfilesLoading] = React.useState(false);
  const [fleetMapperProfilesError, setFleetMapperProfilesError] = React.useState(null);

  // Prefetched trips (parallel with drivers) — used for driver trip counts + selective start
  const [previewTrips, setPreviewTrips] = React.useState([]);
  const [previewTripsLoading, setPreviewTripsLoading] = React.useState(false);
  const [previewTripsError, setPreviewTripsError] = React.useState(null);
  const [previewStatusCounts, setPreviewStatusCounts] = React.useState({});
  const [previewExcludedBreakTrips, setPreviewExcludedBreakTrips] = React.useState(0);
  const [previewLoadUnloadTimes, setPreviewLoadUnloadTimes] = React.useState({});
  const [previewServiceAreaMap, setPreviewServiceAreaMap] = React.useState({});
  const [previewCooperateNames, setPreviewCooperateNames] = React.useState({});

  const normalizeTripId = React.useCallback((t) => {
    const v = t?.trip_object_id || t?.trip_id;
    if (!v) return '';
    if (typeof v === 'object') return String(v._id || v.id || v.$oid || '').trim();
    return String(v).trim();
  }, []);

  const normalizeDriverId = React.useCallback((v) => {
    if (!v) return '';
    if (typeof v === 'object') return String(v._id || v.id || v.$oid || '').trim();
    return String(v).trim();
  }, []);

  const previewFetchGenRef = React.useRef(0);
  const previewTripsInFlightRef = React.useRef(false);
  const getAuthTokenRef = React.useRef(getAuthToken);
  React.useEffect(() => {
    getAuthTokenRef.current = getAuthToken;
  }, [getAuthToken]);

  const handleNonNegativeNumberChange = (key, value, parse = parseFloat) => {
    if (value === '') {
      handleConfigChange(key, '');
      return;
    }
    const parsed = parse(value);
    if (!Number.isNaN(parsed)) {
      handleConfigChange(key, Math.max(0, parsed));
    }
  };

  const handleNonNegativeNumberBlur = (key, value) => {
    if (value === '' || Number.isNaN(parseFloat(value))) {
      handleConfigChange(key, 0);
    }
  };

  /** Violation sets: integers only, 0 or greater (no -, ., e, symbols). */
  const handleNonNegativeIntChange = (key, value) => {
    if (value === '') {
      handleConfigChange(key, '');
      return;
    }
    if (!/^\d+$/.test(value)) return;
    handleConfigChange(key, parseInt(value, 10));
  };

  const handleNonNegativeIntBlur = (key, value) => {
    if (value === '' || !/^\d+$/.test(String(value))) {
      handleConfigChange(key, 0);
      return;
    }
    handleConfigChange(key, parseInt(String(value), 10));
  };

  const mapperShiftsEnabled = React.useMemo(
    () => !!(configFormData.mapper_shifts_enabled ?? currentConfig?.mapper_shifts_enabled ?? false),
    [configFormData.mapper_shifts_enabled, currentConfig?.mapper_shifts_enabled],
  );

  const selectedPlannerMapperId = React.useMemo(
    () => configFormData.selected_planner_mapper_id
      ?? currentConfig?.selected_planner_mapper_id
      ?? '',
    [configFormData.selected_planner_mapper_id, currentConfig?.selected_planner_mapper_id],
  );

  // Reset internal tab logic when opened
  React.useEffect(() => {
    if (!isOpen) return;
    previewFetchGenRef.current += 1;
    previewTripsInFlightRef.current = false;
    setActiveRightTab('drivers');
    setDriverSelection(null);
    setLiveDriverSelection(null);
    setSelectedTripIds([]);
    setUseTripSelection(false);
    setUseCapPlan(false);
    setUseFleetMapping(false);
    setFleetMappingStartError('');
    setPreviewTrips([]);
    setPreviewTripsLoading(false);
    setPreviewTripsError(null);
    setPreviewStatusCounts({});
    setPreviewExcludedBreakTrips(0);
    setPreviewLoadUnloadTimes({});
    setPreviewServiceAreaMap({});
    setPreviewCooperateNames({});
  }, [isOpen]);

  // Keep trip selection logic consistent
  React.useEffect(() => {
    if (!useTripSelection && activeRightTab === 'trips') setActiveRightTab('drivers');
  }, [useTripSelection, activeRightTab]);

  React.useEffect(() => {
    if (!useCapPlan && activeRightTab === 'capplan') setActiveRightTab('drivers');
  }, [useCapPlan, activeRightTab]);

  React.useEffect(() => {
    if (!useFleetMapping && activeRightTab === 'fleetmapping') setActiveRightTab('drivers');
  }, [useFleetMapping, activeRightTab]);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await schedulerApiFetch('fleet-mapping/status', { cacheBust: true });
        const data = await response.json().catch(() => ({}));
        if (!cancelled) setFleetMappingAvailable(response.ok && !!data.enabled);
      } catch {
        if (!cancelled) setFleetMappingAvailable(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  React.useEffect(() => {
    if (!fleetMappingAvailable) setUseFleetMapping(false);
  }, [fleetMappingAvailable]);

  const closeDrawer = () => {
    capPlanFlushRef.current?.();
    fleetMappingFlushRef.current?.();
    onClose?.();
  };

  React.useEffect(() => {
    if (!isOpen || !mapperShiftsEnabled || !companyDetails) {
      if (!mapperShiftsEnabled) {
        setFleetMapperProfiles([]);
        setFleetMapperProfilesError(null);
      }
      return;
    }

    let cancelled = false;
    (async () => {
      setFleetMapperProfilesLoading(true);
      setFleetMapperProfilesError(null);
      try {
        const authToken = getAuthToken ? await getAuthToken() : null;
        const response = await schedulerApiFetch('planner-fleet-mappers', {
          bearerToken: authToken || undefined,
          cacheBust: true,
        });
        const data = await response.json();
        if (cancelled) return;
        if (!response.ok || !data.success) {
          throw new Error(data.message || `Failed to load mapper profiles (${response.status})`);
        }
        setFleetMapperProfiles(data.mappers || []);
      } catch (err) {
        if (!cancelled) {
          setFleetMapperProfiles([]);
          setFleetMapperProfilesError(err.message || 'Failed to load mapper profiles');
        }
      } finally {
        if (!cancelled) setFleetMapperProfilesLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, mapperShiftsEnabled, companyDetails, getAuthToken, syncTrigger]);

  const planningStartForApi = React.useMemo(() => {
    if (use2HourWindow) return null;
    return useCustomPlanningTimes && customPlanningDate ? customPlanningDate : null;
  }, [use2HourWindow, useCustomPlanningTimes, customPlanningDate]);

  const fleetMappingWeekday = React.useMemo(() => {
    const base = planningStartForApi || companyCurrentTime || new Date();
    return dayjs(base).tz(companyTimezone || 'America/Los_Angeles').day();
  }, [planningStartForApi, companyCurrentTime, companyTimezone]);

  const handleFleetMappingSaved = React.useCallback((weekday) => {
    if (weekday !== fleetMappingWeekday) return;
    fleetMappingChangedRef.current = true;
    resetDriverSelection();
  }, [fleetMappingWeekday, resetDriverSelection]);

  const planningEndForApi = React.useMemo(() => {
    if (use2HourWindow) return customPlanningEndDate ?? null;
    return useCustomPlanningTimes && customPlanningEndDate ? customPlanningEndDate : null;
  }, [use2HourWindow, useCustomPlanningTimes, customPlanningEndDate]);

  const fetchPreviewTrips = React.useCallback(async (options = {}) => {
    const force = options === true || options?.force === true;
    // Don't restart an in-flight prefetch (e.g. opening trips tab mid-fetch).
    // Forced refresh / date-mapper changes pass { force: true }.
    if (!force && previewTripsInFlightRef.current) return;

    const gen = ++previewFetchGenRef.current;
    previewTripsInFlightRef.current = true;
    setPreviewTripsLoading(true);
    setPreviewTripsError(null);
    try {
      const params = new URLSearchParams();
      if (planningStartForApi) params.append('custom_datetime_iso', planningStartForApi.toISOString());
      if (planningEndForApi) params.append('custom_datetime_end_iso', planningEndForApi.toISOString());
      params.append('_t', Date.now());

      const authToken = getAuthTokenRef.current ? await getAuthTokenRef.current() : null;

      const [tripsRes, spaceRes, serviceAreaRes] = await Promise.all([
        schedulerApiFetch(`preview-trips?${params.toString()}`, {
          bearerToken: authToken || undefined,
        }),
        schedulerApiFetch('space-types', {
          cacheBust: true,
          bearerToken: authToken || undefined,
        }),
        schedulerApiFetch('service-areas', {
          cacheBust: true,
          bearerToken: authToken || undefined,
        }),
      ]);

      if (gen !== previewFetchGenRef.current) return;

      if (!tripsRes.ok) throw new Error(`Failed to fetch trips: ${tripsRes.status}`);
      const tripsJson = await tripsRes.json();
      if (!tripsJson.success) throw new Error(tripsJson.message || 'Failed to fetch trips');

      const nextTrips = tripsJson.trips || [];
      setPreviewTrips(nextTrips);
      setPreviewStatusCounts(tripsJson.status_counts || {});
      setPreviewExcludedBreakTrips(tripsJson.excluded_break_trips || 0);
      setPreviewCooperateNames(tripsJson.cooperate_names || {});
      // Default-select all trip ids so selective start works without opening the trips tab
      setSelectedTripIds(
        nextTrips
          .map((t) => normalizeTripId(t))
          .filter(Boolean)
      );

      if (spaceRes.ok) {
        const spaceJson = await spaceRes.json();
        if (spaceJson?.success && spaceJson?.load_unload_times) {
          const next = {};
          Object.entries(spaceJson.load_unload_times).forEach(([k, v]) => {
            next[String(k).toLowerCase()] = v;
          });
          setPreviewLoadUnloadTimes(next);
        }
      }

      if (serviceAreaRes.ok) {
        const serviceAreaJson = await serviceAreaRes.json();
        if (serviceAreaJson?.success && serviceAreaJson?.service_areas) {
          const next = {};
          Object.entries(serviceAreaJson.service_areas).forEach(([k, v]) => {
            next[String(k).toLowerCase()] = v;
          });
          setPreviewServiceAreaMap(next);
        }
      }
    } catch (err) {
      if (gen !== previewFetchGenRef.current) return;
      setPreviewTrips([]);
      setSelectedTripIds([]);
      setPreviewStatusCounts({});
      setPreviewExcludedBreakTrips(0);
      setPreviewTripsError(err.message || String(err));
    } finally {
      if (gen === previewFetchGenRef.current) {
        previewTripsInFlightRef.current = false;
        setPreviewTripsLoading(false);
      }
    }
  }, [planningStartForApi, planningEndForApi, normalizeTripId]);

  // Prefetch trips when company is synced — same triggers that refresh drivers
  // (date / mapper / sync). Runs in parallel with DriverSelectionPanel's preview-drivers.
  React.useEffect(() => {
    if (!isOpen || !companyDetails) return;
    // Align with driver panel: wait for mapper profile when mapper shifts are on
    if (mapperShiftsEnabled && !selectedPlannerMapperId) {
      previewFetchGenRef.current += 1;
      previewTripsInFlightRef.current = false;
      setPreviewTrips([]);
      setSelectedTripIds([]);
      setPreviewTripsError(null);
      setPreviewTripsLoading(false);
      return;
    }
    fetchPreviewTrips({ force: true });
  }, [
    isOpen,
    companyDetails,
    syncTrigger,
    planningStartForApi,
    planningEndForApi,
    mapperShiftsEnabled,
    selectedPlannerMapperId,
    fetchPreviewTrips,
  ]);

  const tripCountByDriverId = React.useMemo(() => {
    const counts = {};
    (previewTrips || []).forEach((t) => {
      const did = normalizeDriverId(t.driver_id);
      if (!did) return;
      counts[did] = (counts[did] || 0) + 1;
    });
    return counts;
  }, [previewTrips, normalizeDriverId]);

  const canStart = React.useMemo(() => {
    if (useTripSelection) {
      if (!driverSelection) return false;
      if (!selectedTripIds || selectedTripIds.length === 0) return false;
      return true;
    }
    return liveDriverSelection && liveDriverSelection.driverCount > 0;
  }, [driverSelection, liveDriverSelection, useTripSelection, selectedTripIds]);

  /** Same MM/DD - HH:mm:ss display as `HeaderClock` in Header.js */
  const schedulerClockFormatted = React.useMemo(() => {
    if (!companyCurrentTime) return '';
    const tz = companyTimezone || undefined;
    try {
      const datePart = companyCurrentTime.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        ...(tz ? { timeZone: tz } : {}),
      });
      const timePart = companyCurrentTime.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        ...(tz ? { timeZone: tz } : {}),
      });
      return `${datePart} - ${timePart}`;
    } catch {
      const datePart = companyCurrentTime.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
      const timePart = companyCurrentTime.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
      return `${datePart} - ${timePart}`;
    }
  }, [companyCurrentTime, companyTimezone]);

  const handleDriversConfirm = (selectedDriverIdsOrNull, frozenIds, freezePreAssignIds, perDriverMaxLoad = {}, preservePortalBuckets = false) => {
    setDriverSelection({
      selectedDriverIdsOrNull,
      frozenIds: frozenIds || [],
      freezePreAssignIds: freezePreAssignIds || [],
      perDriverMaxLoad: perDriverMaxLoad && typeof perDriverMaxLoad === 'object' ? perDriverMaxLoad : {},
      preservePortalBuckets: !!preservePortalBuckets,
    });
    if (useTripSelection) {
      setActiveRightTab('trips');
    }
  };

  const handleLiveSelectionChange = React.useCallback((selectionState) => {
    setLiveDriverSelection(selectionState);
  }, []);

  const handleStart = async () => {
    const source = useTripSelection ? driverSelection : liveDriverSelection;
    if (!source) return;
    capPlanFlushRef.current?.();
    setFleetMappingStartError('');
    if (useFleetMapping && fleetMappingFlushRef.current) {
      fleetMappingChangedRef.current = false;
      const saved = await fleetMappingFlushRef.current();
      if (!saved) {
        setFleetMappingStartError('Fleet Mapping has unsaved changes. Fix them before starting.');
        setActiveRightTab('fleetmapping');
        return;
      }
      if (fleetMappingChangedRef.current) {
        setFleetMappingStartError('Fleet Mapping was just saved. Check Select Drivers, then Start.');
        setActiveRightTab('drivers');
        return;
      }
    }
    onStart?.(
      source?.selectedDriverIdsOrNull ?? null,
      source?.frozenIds ?? [],
      source?.freezePreAssignIds ?? [],
      useTripSelection ? selectedTripIds : null,
      source?.perDriverMaxLoad ?? {},
      !!source?.preservePortalBuckets,
      !!useCapPlan,
      !!useFleetMapping
    );
  };

  const renderConfigContent = () => {
    if (!currentConfig) {
      return (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-500">Loading configuration...</p>
        </div>
      );
    }
    const rangeTz = companyTimezone || 'America/Los_Angeles';
    return (
      <div className="flex flex-col flex-1 h-full min-h-0 bg-white">
        <div className="flex-shrink-0 flex items-end border-b border-gray-200 bg-white z-10 px-2 pt-4 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setActiveConfigTab('planner')}
            className={`px-3 py-2.5 font-medium transition-colors border-b-2 text-[13px] whitespace-nowrap ${
              activeConfigTab === 'planner'
                ? 'text-main-primary border-main-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Settings
          </button>
          <button
            onClick={() => setActiveConfigTab('violations')}
            className={`px-3 py-2.5 font-medium transition-colors border-b-2 text-[13px] whitespace-nowrap ${
              activeConfigTab === 'violations'
                ? 'text-main-primary border-main-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Violation Sets
          </button>
          <button
            onClick={() => setActiveConfigTab('company')}
            className={`px-3 py-2.5 font-medium transition-colors border-b-2 text-[13px] whitespace-nowrap ${
              activeConfigTab === 'company'
                ? 'text-main-primary border-main-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Company
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 p-6 space-y-6 pb-24">
          {activeConfigTab === 'planner' && (
            <>
              {leglockNotification && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start">
                  <FiAlertCircle className="text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1 text-sm text-blue-700">{leglockNotification}</div>
                  <button onClick={() => setLeglockNotification('')} className="text-blue-400 hover:text-blue-600">×</button>
                </div>
              )}
              {/* Constraints */}
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-4">Driver Constraints</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Distance per Driver (miles)</label>
                    <input
                      type="number" step="0.1" min={0}
                      value={configFormData.max_distance_per_driver ?? ''}
                      onChange={(e) => handleNonNegativeNumberChange('max_distance_per_driver', e.target.value)}
                      onBlur={(e) => handleNonNegativeNumberBlur('max_distance_per_driver', e.target.value)}
                      className="block w-full px-3 py-2 border rounded-md border-gray-300 shadow-sm focus:border-main-primary focus:ring-main-primary sm:text-sm"
                      placeholder={`${currentConfig.max_distance_per_driver}`}
                    />
                  </div>
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={configFormData.allow_overtime ?? currentConfig.allow_overtime}
                        onChange={(e) => handleConfigChange('allow_overtime', e.target.checked)}
                        className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-4 w-4"
                      />
                      <span className="text-sm font-medium text-gray-700">Allow Overtime</span>
                    </label>
                  </div>
                  {(configFormData.allow_overtime ?? currentConfig.allow_overtime) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Overtime Minutes</label>
                      <input
                        type="number" min={0}
                        value={configFormData.max_overtime_minutes ?? ''}
                        onChange={(e) => handleNonNegativeNumberChange('max_overtime_minutes', e.target.value, (v) => parseInt(v, 10))}
                        onBlur={(e) => handleNonNegativeNumberBlur('max_overtime_minutes', e.target.value)}
                        className="block w-full px-3 py-2 border rounded-md border-gray-300 shadow-sm focus:border-main-primary focus:ring-main-primary sm:text-sm"
                        placeholder={`${currentConfig.max_overtime_minutes}`}
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Trips per Driver</label>
                    <input
                      type="number" min={0}
                      value={configFormData.max_trips_per_driver ?? ''}
                      onChange={(e) => handleNonNegativeNumberChange('max_trips_per_driver', e.target.value, (v) => parseInt(v, 10))}
                      onBlur={(e) => handleNonNegativeNumberBlur('max_trips_per_driver', e.target.value)}
                      className="block w-full px-3 py-2 border rounded-md border-gray-300 shadow-sm focus:border-main-primary focus:ring-main-primary sm:text-sm"
                      placeholder={`${currentConfig.max_trips_per_driver}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Deadhead Miles</label>
                    <input
                      type="number" step="0.1" min={0}
                      value={configFormData.max_deadhead_miles ?? ''}
                      onChange={(e) => handleNonNegativeNumberChange('max_deadhead_miles', e.target.value)}
                      onBlur={(e) => handleNonNegativeNumberBlur('max_deadhead_miles', e.target.value)}
                      className="block w-full px-3 py-2 border rounded-md border-gray-300 shadow-sm focus:border-main-primary focus:ring-main-primary sm:text-sm"
                      placeholder={`${currentConfig.max_deadhead_miles}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Trip Distance (miles)</label>
                    <input
                      type="number" step="0.1" min={0}
                      value={configFormData.max_trip_distance_miles ?? ''}
                      onChange={(e) => handleNonNegativeNumberChange('max_trip_distance_miles', e.target.value)}
                      onBlur={(e) => handleNonNegativeNumberBlur('max_trip_distance_miles', e.target.value)}
                      className="block w-full px-3 py-2 border rounded-md border-gray-300 shadow-sm focus:border-main-primary focus:ring-main-primary sm:text-sm"
                      placeholder={`${currentConfig.max_trip_distance_miles}`}
                    />
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={configFormData.away_from_home_enabled ?? currentConfig?.away_from_home_enabled ?? false}
                        onChange={(e) => {
                          handleConfigChange('away_from_home_enabled', e.target.checked);
                          // Threshold stays fixed at 0 (whole shift); not exposed in UI
                          handleConfigChange('away_from_home_shift_threshold_pct', 0);
                        }}
                        className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-4 w-4"
                      />
                      <span className="text-sm font-medium text-gray-700">Away From Home</span>
                    </label>
                  </div>
                  {(configFormData.away_from_home_enabled ?? currentConfig?.away_from_home_enabled ?? false) && (
                    <div className="ml-6 pl-3 border-l-2 border-gray-200">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Home Distance Radius (miles)</label>
                      <input
                        type="number" step="0.1" min={0}
                        value={configFormData.away_from_home_radius_miles ?? ''}
                        onChange={(e) => handleNonNegativeNumberChange('away_from_home_radius_miles', e.target.value)}
                        onBlur={(e) => handleNonNegativeNumberBlur('away_from_home_radius_miles', e.target.value)}
                        className="block w-full px-3 py-2 border rounded-md border-gray-300 shadow-sm focus:border-main-primary focus:ring-main-primary sm:text-sm"
                        placeholder={`${currentConfig?.away_from_home_radius_miles ?? 10.0}`}
                      />
                    </div>
                  )}
                  {(() => {
                    const leglockOn = configFormData.leglock_enabled ?? currentConfig?.leglock_enabled ?? false;
                    return (
                      <>
                        <div className={leglockOn ? 'opacity-50' : ''}>
                          <label className={`flex items-center space-x-2 ${leglockOn ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                            <input
                              type="checkbox"
                              checked={configFormData.allow_exceed_max_distance ?? currentConfig.allow_exceed_max_distance}
                              disabled={leglockOn}
                              onChange={(e) => { handleConfigChange('allow_exceed_max_distance', e.target.checked); setLeglockNotification(''); }}
                              className={`rounded border-gray-300 text-main-primary focus:ring-main-primary h-4 w-4 ${leglockOn ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            />
                            <span className="text-sm font-medium text-gray-700">Allow Exceed Max Distance</span>
                          </label>
                          {leglockOn && <p className="text-[10px] text-orange-500 font-semibold ml-6 mt-0.5">Blocked by LegLock</p>}
                        </div>
                        <div className={leglockOn ? 'opacity-50' : ''}>
                          <label className={`flex items-center space-x-2 ${leglockOn ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                            <input
                              type="checkbox"
                              checked={configFormData.allow_exceed_max_deadhead ?? currentConfig.allow_exceed_max_deadhead}
                              disabled={leglockOn}
                              onChange={(e) => { handleConfigChange('allow_exceed_max_deadhead', e.target.checked); setLeglockNotification(''); }}
                              className={`rounded border-gray-300 text-main-primary focus:ring-main-primary h-4 w-4 ${leglockOn ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            />
                            <span className="text-sm font-medium text-gray-700">Allow Exceed Max Deadhead</span>
                          </label>
                          {leglockOn && <p className="text-[10px] text-orange-500 font-semibold ml-6 mt-0.5">Blocked by LegLock</p>}
                        </div>
                        <div className={leglockOn ? 'opacity-50' : ''}>
                          <label className={`flex items-center space-x-2 ${leglockOn ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                            <input
                              type="checkbox"
                              checked={configFormData.allow_exceed_max_trip_distance ?? currentConfig.allow_exceed_max_trip_distance}
                              disabled={leglockOn}
                              onChange={(e) => { handleConfigChange('allow_exceed_max_trip_distance', e.target.checked); setLeglockNotification(''); }}
                              className={`rounded border-gray-300 text-main-primary focus:ring-main-primary h-4 w-4 ${leglockOn ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            />
                            <span className="text-sm font-medium text-gray-700">Allow Exceed Max Trip Distance</span>
                          </label>
                          {leglockOn && <p className="text-[10px] text-orange-500 font-semibold ml-6 mt-0.5">Blocked by LegLock</p>}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
              {adminModeUnlocked ? (
                <>
                  <hr className="border-gray-200" />
                  {/* Run interval: same Shift+V gate as Auto-Window (see keyboard handler) */}
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-4">Scheduler Settings</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Run Interval (minutes)</label>
                        <input
                          type="number" min="1" max="1440"
                          value={configFormData.interval_minutes ?? currentConfig?.interval_minutes ?? ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleConfigChange('interval_minutes', value === '' ? '' : parseInt(value) || '');
                          }}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-main-primary focus:ring-main-primary sm:text-sm"
                          placeholder="30"
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
              <hr className="border-gray-200" />

              {/* Optimization */}
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-4">Planner Types</h3>
                <div className="space-y-4 mb-6">
                  {(() => {
                    const combinedOn = configFormData.combined_planner_enabled ?? currentConfig.combined_planner_enabled ?? false;
                    return (
                      <div>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={combinedOn}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              handleConfigChange('combined_planner_enabled', isChecked);
                              if (isChecked) {
                                handleConfigChange('leglock_enabled', false);
                                handleConfigChange('multiload_enabled', false);
                                handleConfigChange('local_optimization_enabled', false);
                              }
                            }}
                            className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-4 w-4"
                          />
                          <span className="text-sm font-medium text-gray-700 inline-flex items-center gap-1">
                            <Truck className="w-4 h-4 shrink-0" />
                            Combined Planner (LegLock + Multiload)
                          </span>
                        </label>
                        {combinedOn && (
                          <div className="mt-2 ml-6 pl-3 border-l-2 border-gray-200">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Max Multiload Distance (miles)</label>
                            <input
                              type="number"
                              step="0.1"
                              min={0}
                              value={configFormData.multiload_max_distance_miles ?? currentConfig.multiload_max_distance_miles ?? ''}
                              onChange={(e) => handleNonNegativeNumberChange('multiload_max_distance_miles', e.target.value)}
                              onBlur={(e) => handleNonNegativeNumberBlur('multiload_max_distance_miles', e.target.value)}
                              className="block w-full px-3 py-2 border rounded-md border-gray-300 shadow-sm focus:border-main-primary focus:ring-main-primary sm:text-sm"
                              placeholder={`${currentConfig.multiload_max_distance_miles ?? 2}`}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Used by Combined planner during group and single multiload proximity checks.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {(() => {
                    const combinedOn = configFormData.combined_planner_enabled ?? currentConfig.combined_planner_enabled ?? false;
                    const multiloadOn = configFormData.multiload_enabled ?? currentConfig.multiload_enabled ?? false;
                    const leglockDisabled = combinedOn || multiloadOn;
                    return (
                      <div className={leglockDisabled ? 'opacity-50' : ''}>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={configFormData.leglock_enabled ?? currentConfig.leglock_enabled ?? false}
                            disabled={leglockDisabled}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (isChecked && (combinedOn || multiloadOn)) return;
                              handleConfigChange('leglock_enabled', isChecked);
                              if (isChecked) {
                                handleConfigChange('combined_planner_enabled', false);
                                handleConfigChange('multiload_enabled', false);
                                handleConfigChange('local_optimization_enabled', false);
                                
                                const currentAllowExceedMaxDistance = configFormData.allow_exceed_max_distance ?? currentConfig.allow_exceed_max_distance;
                                const currentAllowExceedMaxDeadhead = configFormData.allow_exceed_max_deadhead ?? currentConfig.allow_exceed_max_deadhead;
                                const currentAllowExceedMaxTripDistance = configFormData.allow_exceed_max_trip_distance ?? currentConfig.allow_exceed_max_trip_distance;
                                
                                if (currentAllowExceedMaxDistance || currentAllowExceedMaxDeadhead || currentAllowExceedMaxTripDistance) {
                                  handleConfigChange('allow_exceed_max_distance', false);
                                  handleConfigChange('allow_exceed_max_deadhead', false);
                                  handleConfigChange('allow_exceed_max_trip_distance', false);
                                  setLeglockNotification('Distance constraints automatically disabled when LegLock Planner is enabled.');
                                }
                              }
                            }}
                            className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-4 w-4"
                          />
                          <span className="text-sm font-medium text-gray-700">LegLock Planner (Multi-Leg Grouping)</span>
                        </label>
                        {leglockDisabled && <p className="text-[10px] text-orange-500 font-semibold ml-6 mt-0.5">Blocked by {combinedOn ? 'Combined Planner' : 'Multiload'}</p>}
                      </div>
                    );
                  })()}
                  {(() => {
                    const combinedOn = configFormData.combined_planner_enabled ?? currentConfig.combined_planner_enabled ?? false;
                    const leglockOn = configFormData.leglock_enabled ?? currentConfig.leglock_enabled ?? false;
                    const multiloadDisabled = combinedOn || leglockOn;
                    return (
                      <div className={multiloadDisabled ? 'opacity-50' : ''}>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={configFormData.multiload_enabled ?? currentConfig.multiload_enabled ?? false}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (isChecked && (combinedOn || leglockOn)) return;
                              if (isChecked) handleConfigChange('local_optimization_enabled', false);
                              handleConfigChange('multiload_enabled', isChecked);
                            }}
                            disabled={multiloadDisabled}
                            className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-4 w-4"
                          />
                          <span className="text-sm font-medium text-gray-700 inline-flex items-center gap-1">
                            <Truck className="w-4 h-4 shrink-0" />
                            Multiload Planner
                          </span>
                        </label>
                        {multiloadDisabled && <p className="text-[10px] text-orange-500 font-semibold ml-6 mt-0.5">Blocked by {combinedOn ? 'Combined Planner' : 'LegLock'}</p>}
                        {!multiloadDisabled && (configFormData.multiload_enabled ?? currentConfig.multiload_enabled ?? false) && (
                          <div className="mt-2 ml-6 pl-3 border-l-2 border-gray-200">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Max Multiload Distance (miles)</label>
                            <input
                              type="number"
                              step="0.1"
                              min={0}
                              value={configFormData.multiload_max_distance_miles ?? currentConfig.multiload_max_distance_miles ?? ''}
                              onChange={(e) => handleNonNegativeNumberChange('multiload_max_distance_miles', e.target.value)}
                              onBlur={(e) => handleNonNegativeNumberBlur('multiload_max_distance_miles', e.target.value)}
                              className="block w-full px-3 py-2 border rounded-md border-gray-300 shadow-sm focus:border-main-primary focus:ring-main-primary sm:text-sm"
                              placeholder={`${currentConfig.multiload_max_distance_miles ?? 2}`}
                            />
                            <p className="text-xs text-gray-500 mt-1">Max distance between pickup/dropoff locations for multiload</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-4">Optimization Plugins</h3>
                <div className="space-y-4">
                  {(() => {
                    const combinedOn = configFormData.combined_planner_enabled ?? currentConfig.combined_planner_enabled ?? false;
                    const leglockOn = configFormData.leglock_enabled ?? currentConfig.leglock_enabled ?? false;
                    const multiloadOn = configFormData.multiload_enabled ?? currentConfig.multiload_enabled ?? false;
                    const forcePreferredOn = configFormData.force_preferred_driver ?? currentConfig.force_preferred_driver ?? false;
                    const localOptDisabled = combinedOn || leglockOn || multiloadOn || forcePreferredOn;
                    const blockedBy = [combinedOn && 'Combined Planner', leglockOn && 'LegLock', multiloadOn && 'Multiload', forcePreferredOn && 'Force Preferred Driver'].filter(Boolean).join(', ');
                    return (
                      <div className={localOptDisabled ? 'opacity-50' : ''}>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={configFormData.local_optimization_enabled ?? currentConfig.local_optimization_enabled ?? true}
                            onChange={(e) => handleConfigChange('local_optimization_enabled', e.target.checked)}
                            disabled={localOptDisabled}
                            className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-4 w-4"
                          />
                          <span className="text-sm font-medium text-gray-700">Local Optimization (Trip Swapping)</span>
                        </label>
                        {localOptDisabled && <p className="text-[10px] text-orange-500 font-semibold ml-6 mt-0.5">Blocked by {blockedBy}</p>}
                      </div>
                    );
                  })()}
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={configFormData.late_trip_detection_enabled ?? currentConfig.late_trip_detection_enabled ?? false}
                        onChange={(e) => handleConfigChange('late_trip_detection_enabled', e.target.checked)}
                        className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-4 w-4"
                      />
                      <span className="text-sm font-medium text-gray-700">Late Trip Detection</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={configFormData.trip_assumption_enabled ?? currentConfig.trip_assumption_enabled ?? true}
                        onChange={(e) => handleConfigChange('trip_assumption_enabled', e.target.checked)}
                        className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-4 w-4"
                      />
                      <span className="text-sm font-medium text-gray-700">Trip Assumption (Multi-Leg Prediction)</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={configFormData.enable_break_trips ?? currentConfig.enable_break_trips ?? false}
                        onChange={(e) => handleConfigChange('enable_break_trips', e.target.checked)}
                        className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-4 w-4"
                      />
                      <span className="text-sm font-medium text-gray-700">Break Trip Processing</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={configFormData.mapper_shifts_enabled ?? currentConfig.mapper_shifts_enabled ?? false}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          handleConfigChange('mapper_shifts_enabled', isChecked);
                          if (!isChecked) {
                            handleConfigChange('selected_planner_mapper_id', '');
                          }
                        }}
                        className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-4 w-4"
                      />
                      <span className="text-sm font-medium text-gray-700">Mapper</span>
                    </label>
                    {/* <p className="text-xs text-gray-500 mt-1 ml-6">
                      Use planner mapper APIs for shift schedules and fleet assignments instead of driver profile defaults
                    </p> */}
                    {mapperShiftsEnabled && (
                      <div className="ml-6 mt-2 space-y-1">
                        <label className="text-xs font-medium text-gray-600" htmlFor="fleet-mapper-profile">
                          Fleet mapper profile
                        </label>
                        {fleetMapperProfilesLoading ? (
                          <p className="text-xs text-gray-500">Loading mapper profiles…</p>
                        ) : fleetMapperProfilesError ? (
                          <p className="text-xs text-red-600">{fleetMapperProfilesError}</p>
                        ) : (
                          <select
                            id="fleet-mapper-profile"
                            value={selectedPlannerMapperId || ''}
                            onChange={(e) => handleConfigChange('selected_planner_mapper_id', e.target.value || '')}
                            className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white text-gray-900"
                          >
                            <option value="">Select a mapper…</option>
                            {fleetMapperProfiles.map((profile) => (
                              <option key={profile.id} value={profile.id}>
                                {profile.title || profile.id}
                              </option>
                            ))}
                          </select>
                        )}
                        {!selectedPlannerMapperId && !fleetMapperProfilesLoading && (
                          <p className="text-xs text-amber-700">Select a profile before loading drivers.</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={configFormData.use_gis_api ?? currentConfig.use_gis_api ?? false}
                        onChange={(e) => handleConfigChange('use_gis_api', e.target.checked)}
                        className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-4 w-4"
                      />
                      <span className="text-sm font-medium text-gray-700">Enable GIS</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={configFormData.force_assignment ?? false}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          handleConfigChange('force_assignment', isChecked);
                          if (isChecked) {
                            handleConfigChange('force_ignore_service_area', true);
                            handleConfigChange('force_ignore_fleet_compatibility', true);
                            handleConfigChange('force_ignore_funding_source', true);
                            handleConfigChange('force_ignore_shift_time', true);
                          }
                        }}
                        className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-4 w-4"
                      />
                      <span className="text-sm font-medium text-gray-700">Force Assignment</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 mb-2 ml-6">Bypass specific hard constraints during planning</p>
                    
                    {(configFormData.force_assignment ?? currentConfig.force_assignment ?? false) && (
                      <div className="ml-6 space-y-2 border-l-2 border-gray-200 pl-3">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={configFormData.force_ignore_service_area ?? false}
                            onChange={(e) => handleConfigChange('force_ignore_service_area', e.target.checked)}
                            className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-3 w-3"
                          />
                          <span className="text-xs font-medium text-gray-600">Ignore Service Area</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={configFormData.force_ignore_fleet_compatibility ?? false}
                            onChange={(e) => handleConfigChange('force_ignore_fleet_compatibility', e.target.checked)}
                            className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-3 w-3"
                          />
                          <span className="text-xs font-medium text-gray-600">Ignore Fleet Compatibility</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={configFormData.force_ignore_funding_source ?? false}
                            onChange={(e) => handleConfigChange('force_ignore_funding_source', e.target.checked)}
                            className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-3 w-3"
                          />
                          <span className="text-xs font-medium text-gray-600">Ignore Funding Source</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={configFormData.force_ignore_shift_time ?? false}
                            onChange={(e) => handleConfigChange('force_ignore_shift_time', e.target.checked)}
                            className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-3 w-3"
                          />
                          <span className="text-xs font-medium text-gray-600">Ignore Shift Time</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={configFormData.force_preferred_driver ?? false}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              if (isChecked) handleConfigChange('local_optimization_enabled', false);
                              handleConfigChange('force_preferred_driver', isChecked);
                            }}
                            className="rounded border-gray-300 text-main-primary focus:ring-main-primary h-3 w-3"
                          />
                          <span className="text-xs font-medium text-gray-600">Force Preferred Driver</span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <hr className="border-gray-200" />
              <div className="space-y-4">
                {/* Auto-Window + interval share Shift+V (while drawer is open); see keyboard handler above */}
                {adminModeUnlocked ? (
                <div className="relative group">
                  <input
                    type="checkbox"
                    id="window-toggle-compact"
                    checked={use2HourWindow}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setUse2HourWindow(v);
                      if (v) {
                        setUseCustomPlanningTimes(false);
                        setCustomPlanningDate(null);
                        setCustomPlanningEndDate(get2HourWindowEndTime());
                      } else {
                        setCustomPlanningEndDate(null);
                      }
                    }}
                    className="sr-only peer"
                  />
                  <label
                    htmlFor="window-toggle-compact"
                    className={`flex items-center justify-between p-3 rounded-xl border transition-all duration-200 cursor-pointer overflow-hidden ${
                      use2HourWindow
                        ? 'bg-main-primary border-main-primary shadow-md ring-2 ring-main-primary ring-offset-1'
                        : 'bg-white border-gray-200 hover:border-main-primary/30 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className={`p-1 rounded-md transition-colors ${use2HourWindow ? 'bg-white/20 text-white' : 'bg-main-primary/5 text-main-primary'}`}>
                        <FiClock className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex flex-col">
                        <span className={`font-bold text-[12px] leading-tight ${use2HourWindow ? 'text-white' : 'text-gray-900'}`}>
                          Auto-Window (2 Hrs)
                        </span>
                        <p className={`text-[10px] leading-tight ${use2HourWindow ? 'text-white/90' : 'text-gray-500'}`}>
                          From{' '}
                          <span className={`font-bold underline underline-offset-2 ${use2HourWindow ? 'text-white' : ''}`}>Now</span>
                          {' '}to{' '}
                          <span className={`font-bold ${use2HourWindow ? 'text-white' : ''}`}>+2 Hours</span>
                        </p>
                      </div>
                    </div>

                    <div className={`relative flex items-center justify-center transition-transform duration-300 ${use2HourWindow ? 'scale-110' : 'scale-100'}`}>
                      <div className={`w-8 h-4 rounded-full transition-colors ${use2HourWindow ? 'bg-white/30' : 'bg-gray-200'}`} />
                      <div className={`absolute left-0.5 bg-white w-3 h-3 rounded-full transition-all duration-300 ${use2HourWindow ? 'translate-x-4 shadow-[0_0_8px_white]' : 'translate-x-0'}`} />
                    </div>
                  </label>
                </div>
                ) : null}

                {/* Custom range — same card language as Auto-Window; hidden floating labels to avoid MM/DD/YYYY duplication */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <div
                    className={`rounded-xl border border-gray-200 bg-white p-3 shadow-sm space-y-3 transition-all duration-300 max-w-md ${
                      use2HourWindow ? 'opacity-30 pointer-events-none grayscale' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 border-b border-gray-100 pb-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-xs font-semibold text-gray-900 shrink-0">Custom range</span>
                        <div className="h-px flex-1 bg-gray-100 min-w-[1rem]" />
                      </div>
                      {!use2HourWindow && useCustomPlanningTimes && (customPlanningDate || customPlanningEndDate) ? (
                        <button
                          type="button"
                          onClick={() => {
                            setCustomPlanningDate(null);
                            setCustomPlanningEndDate(null);
                            setUseCustomPlanningTimes(false);
                          }}
                          className="shrink-0 rounded-md border border-gray-200 bg-white px-2 py-1 text-[10px] font-semibold text-gray-600 shadow-sm hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                        >
                          Clear
                        </button>
                      ) : null}
                    </div>

                    <label
                      className={`inline-flex cursor-pointer items-center rounded-lg border px-2 py-1.5 ${use2HourWindow ? 'border-gray-100 bg-gray-50 opacity-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                      title={use2HourWindow ? '' : 'Use custom trip day and start/end times'}
                    >
                      <input
                        type="checkbox"
                        checked={useCustomPlanningTimes}
                        disabled={use2HourWindow}
                        aria-label="Use custom trip day and start/end times"
                        onChange={(e) => {
                          const on = e.target.checked;
                          setUseCustomPlanningTimes(on);
                          if (!on) {
                            setCustomPlanningDate(null);
                            setCustomPlanningEndDate(null);
                          }
                        }}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-main-primary focus:ring-main-primary"
                      />
                    </label>

                    <div
                      className={
                        !useCustomPlanningTimes && !use2HourWindow
                          ? 'pointer-events-none select-none opacity-45'
                          : ''
                      }
                    >
                    {(() => {
                      const planningDay = customPlanningDate ?? customPlanningEndDate;
                      const dayBase = planningDay ? dayjs(planningDay).tz(rangeTz).startOf('day') : null;
                      const startDisplay = customPlanningDate
                        ? dayjs(customPlanningDate).tz(rangeTz)
                        : dayBase;
                      const endDisplay = customPlanningEndDate
                        ? dayjs(customPlanningEndDate).tz(rangeTz)
                        : dayBase
                          ? dayBase.endOf('day')
                          : null;
                      const startHM = startDisplay ? startDisplay.tz(rangeTz).format('HH:mm') : '';
                      const endHM = endDisplay ? endDisplay.tz(rangeTz).format('HH:mm') : '';
                      const parseHM = (hm) => {
                        if (!hm || typeof hm !== 'string') return null;
                        const m = hm.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
                        if (!m) return null;
                        const hh = parseInt(m[1], 10);
                        const mm = parseInt(m[2], 10);
                        if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
                        return { hh, mm };
                      };
                      return (
                        <>
                          <div className="space-y-1.5">
                            <span className="text-[10px] font-medium text-gray-500">Trip day</span>
                            <DatePicker
                              timezone={rangeTz}
                              format="MM/DD/YYYY"
                              value={dayBase}
                              onChange={(v) => {
                                if (!v) {
                                  setCustomPlanningDate(null);
                                  setCustomPlanningEndDate(null);
                                  setUseCustomPlanningTimes(false);
                                  return;
                                }
                                const z = v.tz(rangeTz);
                                setUseCustomPlanningTimes(true);
                                setCustomPlanningDate(z.startOf('day').toDate());
                                setCustomPlanningEndDate(z.endOf('day').toDate());
                              }}
                              minDate={dayjs().tz(rangeTz).startOf('day')}
                              disabled={use2HourWindow || !useCustomPlanningTimes}
                              slotProps={{
                                popper: { sx: { zIndex: 10000 } },
                                textField: {
                                  size: 'small',
                                  hiddenLabel: true,
                                  placeholder: 'MM/DD/YYYY',
                                  fullWidth: true,
                                  error: !!dateValidationError,
                                  inputProps: { 'aria-label': 'Trip day' },
                                  sx: {
                                    maxWidth: 200,
                                    '& .MuiInputBase-root': {
                                      height: 30,
                                      fontSize: '11px',
                                      bgcolor: use2HourWindow ? 'var(--gray-50)' : 'white',
                                      borderRadius: '6px',
                                      '&:hover': { borderColor: 'rgb(var(--color-main-primary))' },
                                      '& fieldset': { borderColor: 'rgb(var(--color-main-base)) !important' },
                                      '&.Mui-focused fieldset': {
                                        borderColor: 'rgb(var(--color-main-primary)) !important',
                                        borderWidth: '1px !important',
                                      },
                                    },
                                    '& .MuiInputBase-input': {
                                      fontWeight: 600,
                                      color: 'rgb(var(--color-main-primary))',
                                      py: '5px',
                                      px: '8px',
                                    },
                                    '& .MuiSvgIcon-root': { fontSize: '16px', color: 'rgb(var(--color-main-primary))' },
                                  },
                                },
                              }}
                            />
                          </div>

                          <div className="border-t border-gray-100 pt-2 space-y-2">
                            <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] font-medium text-gray-500">
                              <div className="flex items-center gap-1.5">
                                <FiClock className="w-3 h-3 text-main-primary shrink-0" aria-hidden />
                                <span>Time window (24h, {rangeTz})</span>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2.5">
                              <div className="flex min-w-[9.5rem] flex-1 flex-col gap-1.5 rounded-lg border border-gray-200 bg-gray-50/80 px-2 py-1.5 shadow-sm">
                                <div className="flex items-center justify-between gap-1">
                                  <span className="text-[9px] font-bold uppercase tracking-wider text-gray-500">Start</span>
                                  <button
                                    type="button"
                                    disabled={use2HourWindow || !dayBase || !useCustomPlanningTimes}
                                    onClick={() => {
                                      if (!dayBase) return;
                                      const start = dayBase.startOf('day');
                                      setCustomPlanningDate(start.toDate());
                                      const endWall = customPlanningEndDate
                                        ? dayjs(customPlanningEndDate).tz(rangeTz)
                                        : dayBase.endOf('day');
                                      setCustomPlanningEndDate(
                                        dayBase.hour(endWall.hour()).minute(endWall.minute()).endOf('minute').toDate()
                                      );
                                    }}
                                    className="shrink-0 rounded px-1.5 py-0.5 text-[9px] font-semibold text-main-primary hover:bg-main-primary/10 disabled:opacity-40 disabled:hover:bg-transparent"
                                  >
                                    Reset
                                  </button>
                                </div>
                                <input
                                  type="time"
                                  step={60}
                                  value={startHM}
                                  onChange={(e) => {
                                    const parsed = parseHM(e.target.value);
                                    if (!parsed || !dayBase) return;
                                    setUseCustomPlanningTimes(true);
                                    const start = dayBase.hour(parsed.hh).minute(parsed.mm).startOf('minute');
                                    setCustomPlanningDate(start.toDate());
                                    const endWall = customPlanningEndDate
                                      ? dayjs(customPlanningEndDate).tz(rangeTz)
                                      : dayBase.endOf('day');
                                    setCustomPlanningEndDate(
                                      dayBase.hour(endWall.hour()).minute(endWall.minute()).endOf('minute').toDate()
                                    );
                                  }}
                                  disabled={use2HourWindow || !planningDay || !useCustomPlanningTimes}
                                  aria-label="Start time"
                                  className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 font-mono text-sm font-semibold text-main-primary shadow-sm focus:border-main-primary focus:outline-none focus:ring-1 focus:ring-main-primary disabled:bg-gray-50 disabled:text-gray-400"
                                />
                              </div>
                              <div className="flex min-w-[9.5rem] flex-1 flex-col gap-1.5 rounded-lg border border-gray-200 bg-gray-50/80 px-2 py-1.5 shadow-sm">
                                <div className="flex items-center justify-between gap-1">
                                  <span className="text-[9px] font-bold uppercase tracking-wider text-gray-500">End</span>
                                  <button
                                    type="button"
                                    disabled={use2HourWindow || !dayBase || !useCustomPlanningTimes}
                                    onClick={() => {
                                      if (!dayBase) return;
                                      const end = dayBase.endOf('day');
                                      setCustomPlanningEndDate(end.toDate());
                                      const startWall = customPlanningDate
                                        ? dayjs(customPlanningDate).tz(rangeTz)
                                        : dayBase.startOf('day');
                                      setCustomPlanningDate(
                                        dayBase.hour(startWall.hour()).minute(startWall.minute()).startOf('minute').toDate()
                                      );
                                    }}
                                    className="shrink-0 rounded px-1.5 py-0.5 text-[9px] font-semibold text-main-primary hover:bg-main-primary/10 disabled:opacity-40 disabled:hover:bg-transparent"
                                  >
                                    Reset
                                  </button>
                                </div>
                                <input
                                  type="time"
                                  step={60}
                                  value={endHM}
                                  onChange={(e) => {
                                    const parsed = parseHM(e.target.value);
                                    if (!parsed || !dayBase) return;
                                    setUseCustomPlanningTimes(true);
                                    const end = dayBase.hour(parsed.hh).minute(parsed.mm).endOf('minute');
                                    setCustomPlanningEndDate(end.toDate());
                                    const startWall = customPlanningDate
                                      ? dayjs(customPlanningDate).tz(rangeTz)
                                      : dayBase.startOf('day');
                                    setCustomPlanningDate(
                                      dayBase.hour(startWall.hour()).minute(startWall.minute()).startOf('minute').toDate()
                                    );
                                  }}
                                  disabled={use2HourWindow || !planningDay || !useCustomPlanningTimes}
                                  aria-label="End time"
                                  className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 font-mono text-sm font-semibold text-main-primary shadow-sm focus:border-main-primary focus:outline-none focus:ring-1 focus:ring-main-primary disabled:bg-gray-50 disabled:text-gray-400"
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                    </div>

                    {dateValidationError && (
                      <p className="text-[10px] text-red-600 font-semibold flex items-start gap-1.5 pt-0.5 border-t border-red-100">
                        <FiAlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        {dateValidationError}
                      </p>
                    )}
                  </div>
                </LocalizationProvider>
              </div>
            </>
          )}

          {activeConfigTab === 'violations' && (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-4">Violation Sets</h3>
              {[
                {
                  title: 'Leg A',
                  fields: [
                    ['cautions_pickup_early_min', 'Pickup early (minutes)'],
                    ['cautions_pickup_late_min', 'Pickup late (minutes)'],
                    ['cautions_dropoff_late_min', 'Dropoff late (minutes)'],
                    ['cautions_dropoff_early_min', 'Dropoff early (minutes)'],
                  ],
                },
                {
                  title: 'Other legs',
                  fields: [
                    ['cautions_other_pickup_early_min', 'Pickup early (minutes)'],
                    ['cautions_other_pickup_late_min', 'Pickup late (minutes)'],
                    ['cautions_other_dropoff_late_min', 'Dropoff late (minutes)'],
                    ['cautions_other_dropoff_early_min', 'Dropoff early (minutes)'],
                  ],
                },
              ].map((group) => (
                <div key={group.title} className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">{group.title}</h4>
                  <div className="space-y-4">
                    {group.fields.map(([field, label]) => (
                      <div key={field}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {label}
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          autoComplete="off"
                          value={
                            configFormData[field] === '' || configFormData[field] == null
                              ? ''
                              : String(configFormData[field])
                          }
                          onChange={(e) => handleNonNegativeIntChange(field, e.target.value)}
                          onBlur={(e) => handleNonNegativeIntBlur(field, e.target.value)}
                          onKeyDown={(e) => {
                            if (['e', 'E', '+', '-', '.', ','].includes(e.key)) e.preventDefault();
                          }}
                          className="block w-full px-3 py-2 border rounded-md border-gray-300 shadow-sm focus:border-main-primary focus:ring-main-primary sm:text-sm"
                          placeholder={`${currentConfig[field] ?? 0}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeConfigTab === 'company' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base font-semibold text-gray-900">Company Information</h3>

                <button
                  onClick={() => fetchCompanyDetails(true)}
                  disabled={isSyncing}
                  className="flex items-center gap-2 px-3 py-1.5 bg-main-primary text-white rounded shadow-sm hover:bg-main-primary/90 text-sm font-medium transition"
                >
                  <FiRefreshCw className={isSyncing ? 'animate-spin' : ''} />
                  {isSyncing ? 'Syncing...' : 'Sync Data'}
                </button>
              </div>
              {companyDetails && (
                <div className="bg-main-primary/5 border border-main-base rounded-lg p-4 space-y-3">
                  <div className="flex items-start">
                    <FiBriefcase className="text-main-primary mt-0.5 mr-3 w-5 h-5" />
                    <div><div className="text-xs text-main-primary font-medium font-mono uppercase">Name</div><div className="text-sm font-bold text-main-primary">{companyDetails.companyName}</div></div>
                  </div>
                  <div className="flex items-start">
                    <FiMapPin className="text-main-primary mt-0.5 mr-3 w-5 h-5" />
                    <div><div className="text-xs text-main-primary font-medium font-mono uppercase">Address</div><div className="text-sm font-semibold text-main-primary">{companyDetails.address}</div></div>
                  </div>
                </div>
              )}

              {adminModeUnlocked && (
                <>
                  <hr className="border-gray-200" />
                  <div className="space-y-4">
                    <h3 className="text-base font-semibold text-gray-900">API Configuration</h3>

                    {(() => {
                      const nemtBase = getConfigFieldDisplay(
                        'nemt_api_base_url',
                        configFormData,
                        currentConfig,
                        configDirtyFields
                      ).trim();
                      const env = displayedServerEnvironment(
                        nemtBase,
                        configFormData.server_environment ?? currentConfig?.server_environment
                      );
                      const isDev = env === 'dev';
                      return (
                        <div
                          className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-bold ${
                            isDev ? 'bg-orange-50 text-orange-700 ring-1 ring-orange-200' : 'bg-main-primary/10 text-main-primary ring-1 ring-main-primary/20'
                          }`}
                        >
                          <span className={`h-2 w-2 rounded-full ${isDev ? 'bg-orange-500' : 'bg-main-primary'}`} />
                          {isDev ? 'Development' : 'Live'}
                        </div>
                      );
                    })()}

                    {syncMessage?.text && (
                      <div
                        className={`p-3 rounded-lg border text-sm ${
                          syncMessage.type === 'success'
                            ? 'bg-green-50 border-green-200 text-green-800'
                            : 'bg-red-50 border-red-200 text-red-800'
                        }`}
                      >
                        {syncMessage.text}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">JWT Token</label>
                      <input
                        type="password"
                        value={getConfigFieldDisplay(
                          'api_jwt_token',
                          configFormData,
                          currentConfig,
                          configDirtyFields
                        )}
                        onChange={(e) => handleConfigChange('api_jwt_token', e.target.value)}
                        className="block w-full px-3 py-2 border rounded-md border-gray-300 shadow-sm focus:border-main-primary focus:ring-main-primary sm:text-sm font-mono"
                        placeholder="eyJhbG..."
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-sm font-medium text-gray-700">NEMT API base URL</label>
                        <button
                          type="button"
                          onClick={() => saveApiSettings?.()}
                          disabled={isSavingApiSettings}
                          title="Save API settings"
                          className="flex items-center justify-center p-1.5 bg-main-primary text-white rounded shadow-sm hover:bg-main-primary/90 transition disabled:opacity-50"
                        >
                          {isSavingApiSettings ? (
                            <FiRefreshCw className="w-4 h-4 animate-spin" aria-hidden />
                          ) : (
                            <FiSave className="w-4 h-4" aria-hidden />
                          )}
                          <span className="sr-only">Save API settings</span>
                        </button>
                      </div>
                      <input
                        type="text"
                        value={getConfigFieldDisplay(
                          'nemt_api_base_url',
                          configFormData,
                          currentConfig,
                          configDirtyFields
                        )}
                        onChange={(e) => handleConfigChange('nemt_api_base_url', e.target.value)}
                        className="block w-full px-3 py-2 border rounded-md border-gray-300 shadow-sm focus:border-main-primary focus:ring-main-primary sm:text-sm font-mono"
                        placeholder="https://example.nemtplatform.com/api"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const companySynced = !!companyDetails;

  // After hooks: safe to return early without violating rules-of-hooks.
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={() => {}} className="relative z-[2200]">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity duration-500 ease-in-out data-[closed]:opacity-0"
      />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-16">
            <DialogPanel
              transition
              className="pointer-events-auto relative w-screen max-w-full transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700 flex flex-col bg-white shadow-2xl"
            >
              <TransitionChild>
                <div className="absolute left-0 top-0 -ml-8 flex pr-2 pt-4 duration-500 ease-in-out data-[closed]:opacity-0 sm:-ml-10 sm:pr-4">
                  <button
                    type="button"
                    onClick={closeDrawer}
                    className="relative rounded-md text-gray-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                  >
                    <span className="absolute -inset-2.5" />
                    <span className="sr-only">Close panel</span>
                    <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                  </button>
                </div>
              </TransitionChild>

              {/* Drawer Header */}
              <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-4 shadow-sm z-20">
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="bg-main-primary p-2 rounded-lg shadow-sm shrink-0">
                    <Truck className="h-5 w-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-bold text-gray-900 leading-tight">Scheduler Setup</h2>
                    <p className="text-xs text-gray-500 font-medium">Configure rules, choose drivers, and pick trips.</p>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 rounded-full px-3 py-2 bg-main-primary/5 w-48 shrink-0 ring-1 ring-header-primary/10">
                  <div className="hidden sm:block">
                    <svg
                      className="w-5 h-5 text-[#505665]"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="text-[10px] sm:text-xs font-bold text-main-primary/80 sm:tracking-[2px] whitespace-nowrap">
                    {schedulerClockFormatted}
                  </div>
                </div>
              </div>

              {/* Drawer Body Area - Split View */}
              <div className="flex-1 overflow-hidden bg-gray-100 flex min-h-0">
                
                {/* Left Pane - Configuration */}
                <div className="w-[360px] flex-shrink-0 border-r border-gray-200 bg-white flex flex-col h-full min-h-0 shadow-sm z-10">
                  {renderConfigContent()}
                </div>

                {/* Right Pane - Drivers & Trips */}
                <div className="flex-1 flex flex-col h-full overflow-hidden bg-gray-50/50">
                  {/* Right Pane Header / Tabs */}
                  <div className="flex px-4 py-2 border-b border-gray-200 bg-white items-center justify-between shadow-sm">
                    <div className="flex space-x-2 bg-gray-100/80 p-1 rounded-lg">
                       <button
                        onClick={() => setActiveRightTab('drivers')}
                        className={`px-4 py-1.5 rounded-md transition-all duration-200 font-semibold text-sm ${activeRightTab === 'drivers' ? 'bg-white text-main-primary shadow-sm border border-gray-200/60' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`}
                      >
                        1. Select Drivers
                      </button>
                      <button
                        onClick={() => driverSelection && useTripSelection && setActiveRightTab('trips')}
                        disabled={!driverSelection || !useTripSelection}
                        className={`px-4 py-1.5 rounded-md transition-all duration-200 font-semibold text-sm ${activeRightTab === 'trips' ? 'bg-white text-main-primary shadow-sm border border-gray-200/60' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'} ${(!driverSelection || !useTripSelection) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title={!driverSelection ? 'Confirm drivers first' : !useTripSelection ? 'Turn on Use Trip Selection' : ''}
                      >
                        2. Select Trips
                      </button>
                      <button
                        onClick={() => useCapPlan && setActiveRightTab('capplan')}
                        disabled={!useCapPlan}
                        className={`px-4 py-1.5 rounded-md transition-all duration-200 font-semibold text-sm ${activeRightTab === 'capplan' ? 'bg-white text-main-primary shadow-sm border border-gray-200/60' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'} ${!useCapPlan ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title={!useCapPlan ? 'Turn on Use Cap Plan' : ''}
                      >
                        Cap Plan
                      </button>
                      {fleetMappingAvailable && (
                        <button
                          onClick={() => useFleetMapping && setActiveRightTab('fleetmapping')}
                          disabled={!useFleetMapping}
                          className={`px-4 py-1.5 rounded-md transition-all duration-200 font-semibold text-sm ${activeRightTab === 'fleetmapping' ? 'bg-white text-main-primary shadow-sm border border-gray-200/60' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'} ${!useFleetMapping ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={!useFleetMapping ? 'Turn on Use Fleet Mapping' : ''}
                        >
                          Fleet Mapping
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Right Pane Content */}
                  <div className="flex-1 overflow-y-auto">
                    {activeRightTab === 'drivers' && (
                      <div className="h-full flex flex-col p-4">
                        {!companySynced ? (
                          <div className="h-full flex flex-col items-center justify-center text-center p-10 bg-white rounded-xl border border-gray-200">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                            <div className="text-sm font-semibold text-gray-900">Syncing company details…</div>
                            <div className="text-xs text-gray-500 mt-1 max-w-sm">
                              Drivers will load after company sync completes.
                            </div>
                          </div>
                        ) : (
                          <DriverSelectionPanel
                            isOpen={true}
                            onClose={null}
                            onConfirm={handleDriversConfirm}
                            schedulerApiUrl={schedulerApiUrl}
                            customPlanningDate={planningStartForApi}
                            getAuthToken={getAuthToken}
                            companyTimezone={companyTimezone}
                            syncTrigger={syncTrigger}
                            embedded
                            showConfirmButton={useTripSelection}
                            confirmButtonLabel="Go to Trip Selection"
                            onSelectionChange={handleLiveSelectionChange}
                            tripCountByDriverId={tripCountByDriverId}
                            tripsCountLoading={previewTripsLoading}
                            driverTemplateCompanyId={configFormData?.company_id ?? currentConfig?.company_id ?? companyDetails?.companyId ?? ''}
                            mapperShiftsEnabled={mapperShiftsEnabled}
                            selectedPlannerMapperId={selectedPlannerMapperId || null}
                            useFleetMapping={useFleetMapping}
                            driverTemplateServerEnvironment={displayedServerEnvironment(
                              getConfigFieldDisplay(
                                'nemt_api_base_url',
                                configFormData,
                                currentConfig,
                                configDirtyFields
                              ),
                              configFormData?.server_environment ?? currentConfig?.server_environment
                            )}
                          />
                        )}
                      </div>
                    )}
                    {activeRightTab === 'trips' && (
                      <div className="h-full flex flex-col pb-4">
                        {!companySynced ? (
                          <div className="h-full flex flex-col items-center justify-center text-center p-10 bg-white rounded-xl border border-gray-200 m-4">
                            <div className="text-sm font-semibold text-gray-900">Company sync required</div>
                            <div className="text-xs text-gray-500 mt-1 max-w-sm">
                              Please wait for company details to sync before selecting trips.
                            </div>
                          </div>
                        ) : (
                          <TripsSelectionTab
                            schedulerApiUrl={schedulerApiUrl}
                            customPlanningDate={planningStartForApi}
                            customPlanningEndDate={planningEndForApi}
                            getAuthToken={getAuthToken}
                            companyTimezone={companyTimezone}
                            frozenDriverIds={driverSelection?.frozenIds || []}
                            freezePreAssignDriverIds={driverSelection?.freezePreAssignIds || []}
                            preservePortalBuckets={!!driverSelection?.preservePortalBuckets}
                            selectedDriverIdsOrNull={driverSelection?.selectedDriverIdsOrNull ?? null}
                            syncTrigger={syncTrigger}
                            selectedTripIds={selectedTripIds}
                            onSelectedTripIdsChange={setSelectedTripIds}
                            onRefreshExternal={(opts) => fetchPreviewTrips({ force: true, ...(opts || {}) })}
                            externalTrips={previewTrips}
                            externalStatusCounts={previewStatusCounts}
                            externalExcludedBreakTrips={previewExcludedBreakTrips}
                            externalLoadUnloadTimes={previewLoadUnloadTimes}
                            externalServiceAreaMap={previewServiceAreaMap}
                            externalCooperateNames={previewCooperateNames}
                            externalLoading={previewTripsLoading}
                            externalError={previewTripsError}
                          />
                        )}
                      </div>
                    )}
                    {useCapPlan && (
                      <div className={activeRightTab === 'capplan' ? 'h-full' : 'hidden'}>
                        <CapPlanPanel
                          getAuthToken={getAuthToken}
                          companyId={configFormData?.company_id ?? currentConfig?.company_id ?? companyDetails?.companyId ?? ''}
                          onFlushReady={(fn) => { capPlanFlushRef.current = fn; }}
                        />
                      </div>
                    )}
                    {useFleetMapping && (
                      <div className={activeRightTab === 'fleetmapping' ? 'h-full' : 'hidden'}>
                        <FleetMappingPanel
                          getAuthToken={getAuthToken}
                          defaultWeekday={fleetMappingWeekday}
                          onFlushReady={handleFleetMappingFlushReady}
                          onSaved={handleFleetMappingSaved}
                        />
                      </div>
                    )}
                  </div>
                </div>

              </div>

              {/* Drawer Sticky Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
                <div className="flex items-center gap-4 text-sm font-medium">
                  <label className="flex items-center gap-2 cursor-pointer text-gray-700 bg-gray-50 px-3 py-1.5 rounded-md border border-gray-200">
                    <input
                      type="checkbox"
                      checked={useTripSelection}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setUseTripSelection(on);
                        if (!on) {
                          setDriverSelection(null);
                          setActiveRightTab('drivers');
                        }
                      }}
                      className="rounded text-main-primary border-gray-300 focus:ring-main-primary h-4 w-4"
                    />
                    Use Selective Trips
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-gray-700 bg-gray-50 px-3 py-1.5 rounded-md border border-gray-200">
                    <input
                      type="checkbox"
                      checked={useCapPlan}
                      onChange={(e) => setUseCapPlan(e.target.checked)}
                      className="rounded text-main-primary border-gray-300 focus:ring-main-primary h-4 w-4"
                    />
                    Use Cap Plan
                  </label>
                  {fleetMappingAvailable && (
                    <label className="flex items-center gap-2 cursor-pointer text-gray-700 bg-gray-50 px-3 py-1.5 rounded-md border border-gray-200">
                      <input
                        type="checkbox"
                        checked={useFleetMapping}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setUseFleetMapping(on);
                          setFleetMappingStartError('');
                          resetDriverSelection();
                          if (on) setActiveRightTab('fleetmapping');
                        }}
                        className="rounded text-main-primary border-gray-300 focus:ring-main-primary h-4 w-4"
                      />
                      Use Fleet Mapping
                    </label>
                  )}
                  {fleetMappingStartError ? (
                    <span className="text-xs font-medium text-red-600">{fleetMappingStartError}</span>
                  ) : useFleetMapping && activeRightTab !== 'drivers' && !liveDriverSelection ? (
                    <span className="text-xs font-medium text-amber-700">Open Select Drivers to load fleet-mapped drivers.</span>
                  ) : null}
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={closeDrawer}
                    className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md shadow-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-main-primary"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleStart}
                    disabled={!canStart || isStarting}
                    className="px-6 py-2 bg-main-primary text-white rounded-md shadow-sm font-medium hover:bg-main-primary/90 focus:outline-none focus:ring-2 border border-transparent focus:ring-main-primary disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center"
                  >
                    {isStarting ? (
                      <><div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2"/> Starting...</>
                    ) : (
                      'Start Scheduler'
                    )}
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
